/**
 * KUROGANE — comportamientos globales.
 * Custom Elements reutilizables que cualquier sección puede usar sin JS propio.
 */
(function () {
  'use strict';

  const kg = window.kg;
  if (!kg) return;

  /* ================================================================== *
   * Altura del encabezado → variable CSS para offsets sticky
   * ================================================================== */
  function syncHeaderHeight() {
    const header = document.querySelector('[data-kg-header]');
    const height = header ? header.offsetHeight : 0;
    document.documentElement.style.setProperty('--kg-header-height', `${height}px`);
  }

  window.addEventListener('resize', kg.throttle(syncHeaderHeight, 150));
  document.addEventListener('shopify:section:load', syncHeaderHeight);
  if (document.readyState !== 'loading') syncHeaderHeight();
  else document.addEventListener('DOMContentLoaded', syncHeaderHeight);

  /* ================================================================== *
   * Revelado al hacer scroll
   * ================================================================== */
  function initReveals(root = document) {
    const settings = (window.kgTheme && window.kgTheme.settings) || {};
    if (settings.revealAnimations === false || kg.prefersReducedMotion()) {
      root.querySelectorAll('.kg-reveal').forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const targets = root.querySelectorAll('.kg-reveal:not(.is-revealed)');
    if (!targets.length) return;
    kg.onIntersect(targets, (el) => el.classList.add('is-revealed'), { rootMargin: '0px 0px -8% 0px' });
  }

  if (document.readyState !== 'loading') initReveals();
  else document.addEventListener('DOMContentLoaded', () => initReveals());
  document.addEventListener('shopify:section:load', (event) => initReveals(event.target));

  /* ================================================================== *
   * <kg-accordion> — details/summary con animación de altura accesible
   * ================================================================== */
  class KgAccordion extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.exclusive = this.hasAttribute('exclusive');

      this.addEventListener('toggle', this.onToggle.bind(this), {
        capture: true,
        signal: this.abort.signal
      });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    onToggle(event) {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement)) return;
      if (!this.exclusive || !item.open) return;

      this.querySelectorAll('details[open]').forEach((other) => {
        if (other !== item) other.open = false;
      });
    }
  }
  if (!customElements.get('kg-accordion')) customElements.define('kg-accordion', KgAccordion);

  /* ================================================================== *
   * <kg-carousel> — scroll-snap con botones y estado accesible
   * ================================================================== */
  class KgCarousel extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.track = this.querySelector('[data-carousel-track]');
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      if (!this.track) return;

      this.track.addEventListener('scroll', kg.throttle(() => this.updateButtons(), 120), { signal });
      window.addEventListener('resize', kg.throttle(() => this.updateButtons(), 200), { signal });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1), { signal });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1), { signal });

      this.updateButtons();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    scrollByPage(direction) {
      const slide = this.track.querySelector('.kg-carousel__slide');
      const step = slide ? slide.offsetWidth + 16 : this.track.clientWidth * 0.8;
      this.track.scrollBy({
        left: step * direction,
        behavior: kg.prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    updateButtons() {
      if (!this.prevBtn || !this.nextBtn) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
      // Si no hay desbordamiento, ocultamos la navegación del flujo accesible.
      const overflows = scrollWidth > clientWidth + 4;
      this.querySelectorAll('[data-carousel-nav]').forEach((nav) => { nav.hidden = !overflows; });
    }
  }
  if (!customElements.get('kg-carousel')) customElements.define('kg-carousel', KgCarousel);

  /* ================================================================== *
   * <kg-modal> — diálogo accesible genérico (vídeo, guía de tallas, etc.)
   * Se abre con [data-kg-modal-open="ID"], se cierra con Escape o backdrop.
   * ================================================================== */
  class KgModal extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.content = this.querySelector('.kg-modal__content') || this;
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');

      this.querySelectorAll('[data-kg-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close(), { signal });
      });

      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      }, { signal });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.dataset.open === 'true') this.close();
      }, { signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.dataset.open === 'true') kg.unlockScroll(true);
    }

    open(trigger) {
      this.opener = trigger || document.activeElement;
      this.dataset.open = 'true';
      kg.lockScroll();
      kg.trapFocus(this.content);
      this.dispatchEvent(new CustomEvent('kg:modal:open', { bubbles: true }));
    }

    close() {
      if (this.dataset.open !== 'true') return;
      delete this.dataset.open;
      kg.unlockScroll();
      kg.releaseFocus(this.content);
      if (this.opener && typeof this.opener.focus === 'function') {
        this.opener.focus({ preventScroll: true });
      }
      // Pausamos cualquier vídeo interno al cerrar.
      this.querySelectorAll('video').forEach((v) => v.pause());
      this.querySelectorAll('iframe[src]').forEach((frame) => {
        // eslint-disable-next-line no-self-assign
        frame.src = frame.src;
      });
      this.dispatchEvent(new CustomEvent('kg:modal:close', { bubbles: true }));
    }
  }
  if (!customElements.get('kg-modal')) customElements.define('kg-modal', KgModal);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-kg-modal-open]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.dataset.kgModalOpen);
    if (!modal || typeof modal.open !== 'function') return;
    event.preventDefault();
    modal.open(trigger);
  });

  /* ================================================================== *
   * <kg-video> — carga diferida, autoplay responsable y reduced-motion
   * ================================================================== */
  class KgVideo extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.video = this.querySelector('video');
      this.playBtn = this.querySelector('[data-video-play]');
      const settings = (window.kgTheme && window.kgTheme.settings) || {};

      this.shouldAutoplay =
        this.hasAttribute('autoplay') &&
        settings.videoAutoplay !== false &&
        !kg.prefersReducedMotion() &&
        !this.saveData();

      if (this.playBtn) {
        this.playBtn.addEventListener('click', () => this.play(), { signal: this.abort.signal });
      }

      if (!this.video) return;

      // Nunca cargamos el vídeo hasta que está cerca del viewport.
      kg.onIntersect(this, () => this.load(), { rootMargin: '200px 0px' });

      if (this.shouldAutoplay) {
        // Pausamos fuera de pantalla para no gastar batería ni CPU.
        kg.onIntersect(this, () => {}, { once: false });
        this.visibility = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.video.play().catch(() => {});
            else this.video.pause();
          });
        }, { threshold: 0.25 });
        this.visibility.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.visibility) this.visibility.disconnect();
    }

    saveData() {
      return !!(navigator.connection && navigator.connection.saveData);
    }

    load() {
      if (this.loaded || !this.video) return;
      this.loaded = true;
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      this.video.load();
      if (this.shouldAutoplay) this.video.play().catch(() => {});
    }

    play() {
      this.load();
      this.dataset.playing = 'true';
      if (this.video) {
        this.video.controls = true;
        this.video.play().catch(() => {});
      }
    }
  }
  if (!customElements.get('kg-video')) customElements.define('kg-video', KgVideo);

  /* ================================================================== *
   * <kg-quantity> — selector de cantidad accesible
   * ================================================================== */
  class KgQuantity extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.input = this.querySelector('input[type="number"]');
      if (!this.input) return;

      this.querySelectorAll('[data-quantity-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = parseInt(btn.dataset.quantityStep, 10);
          const min = parseInt(this.input.min || '0', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || 0) + step));
          if (next === parseInt(this.input.value, 10)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }, { signal });
      });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }
  }
  if (!customElements.get('kg-quantity')) customElements.define('kg-quantity', KgQuantity);

  /* ================================================================== *
   * Precarga al pasar el cursor
   * ================================================================== */
  if (window.kgTheme && window.kgTheme.settings && window.kgTheme.settings.prefetch) {
    const prefetched = new Set();
    const prefetch = (url) => {
      if (!url || prefetched.has(url)) return;
      prefetched.add(url);
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      document.head.appendChild(link);
    };

    document.addEventListener('mouseover', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname.startsWith('/cart') || url.pathname.startsWith('/checkout')) return;
      if (link.hasAttribute('download') || link.target === '_blank') return;
      prefetch(url.href);
    }, { passive: true, capture: true });
  }
})();
